import { HOST_COMMENTS } from 'config';
import { rtkApi } from 'redux/rtkApi';
import { Client } from 'types/client';
import { ListResponse } from 'types/common';
import { Comment, CommentComplaintReason } from 'types/comment';

export type ComplaintComment = {
  id: number;
  client: Client | null;
  client_id: number | null;
  comment: Comment | null;
  comment_id: number | null;
  user_id: number | null;
  reason: CommentComplaintReason | null;
};

export type ComplaintCommentList = ListResponse<ComplaintComment>;

export type ComplaintCommentUpdateParams = {
  id: number | string;
};

const commentsComplaints = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    // ---------- GET LIST ----------
    getCommentsComplaintsList: build.query<
      ComplaintCommentList,
      { user_id?: 'y' | 'n'; per_page: number; page?: number }
    >({
      query: (params) => ({
        url: `${HOST_COMMENTS}/comments-complaints`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: ComplaintCommentList) => response,

      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((c) => ({ type: 'CommentsComplaints' as const, id: c.id })),
              { type: 'CommentsComplaints', id: 'LIST' },
            ]
          : [{ type: 'CommentsComplaints', id: 'LIST' }],
    }),

    // ---------- GET BY ID ----------
    getCommentsComplaintsById: build.query<ComplaintComment, { id: number | string }>({
      query: ({ id }) => ({
        url: `${HOST_COMMENTS}/comments-complaints/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { data: ComplaintComment }) => response.data,

      providesTags: (result) =>
        result ? [{ type: 'CommentsComplaints', id: result.id }] : ['CommentsComplaints'],
    }),

    // ---------- PUT ----------
    putCommentsComplaints: build.mutation<ComplaintComment, ComplaintCommentUpdateParams>({
      query: ({ id }) => ({
        url: `${HOST_COMMENTS}/comments-complaints/${id}`,
        method: 'PUT',
      }),
      transformResponse: (response: { data: ComplaintComment }) => response.data,

      invalidatesTags: (result) =>
        result
          ? [
              { type: 'CommentsComplaints', id: result.id },
              { type: 'CommentsComplaints', id: 'LIST' },
            ]
          : [{ type: 'CommentsComplaints', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetCommentsComplaintsListQuery,
  useLazyGetCommentsComplaintsListQuery,
  useGetCommentsComplaintsByIdQuery,
  usePutCommentsComplaintsMutation,
} = commentsComplaints;
